import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import {
    Box,
    Chip,
    Grid,
    Paper,
    Button,
    Tooltip,
    useTheme,
    Typography,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import {
    Info as InfoIcon,
    Source as SourceIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import EmailSourceModal from '../ForReview/EmailSourceModal';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useInventoryService } from 'store/services/inventory.service';
import PurchaseAiAgentMatchingFilters from './PurchaseAiAgentMatchingFilters';
import { DataTable, CircleLoading, TablePagination, SnackBarAlert } from 'shared';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const PurchaseAiAgentMatching = () => {
    const theme = useTheme();
    const companyData = useSelector((state) => state.company.data);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const isXLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
    const { purchaseAgentMatching, oneToOnePurchaseMatching, statusTestAIMatching } =
        useInventoryService();
    const truncateLimit = isXLargeScreen ? 60 : isLargeScreen ? 45 : isMediumScreen ? 25 : 20;

    const [page, setPage] = useState(1);
    const [sorting, setSorting] = useState({});
    const [allData, setAllData] = useState([]);
    const [filters, setFilters] = useState({});
    const [selected, setSelected] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [sortOrder, setSortOrder] = useState('asc');
    const [filteredData, setFilteredData] = useState([]);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [isMakingMatches, setIsMakingMatches] = useState(false);
    const [selectedEmailIds, setSelectedEmailIds] = useState(null);
    const [emailSourceModalOpen, setEmailSourceModalOpen] = useState(false);
    const [showPaymentIdentifier, setShowPaymentIdentifier] = useState(false);

    const handleEmailClick = (emailIds) => {
        if (!emailIds) return;
        setSelectedEmailIds(emailIds);
        setEmailSourceModalOpen(true);
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleMatchSelected = async () => {
        const selectedIds = Object.keys(selected);
        if (selectedIds.length === 0) return;

        const matches = allData
            .filter((row) => selectedIds.includes(String(row.id)))
            .map((row) => ({
                plaid_transaction_id: row.transaction.id,
                aggregated_file_inventory_id: row.inventory.id,
            }));

        const payload = {
            company_id: companyData.id,
            matches,
        };

        try {
            setLoading(true);
            await oneToOnePurchaseMatching(payload);
            handleSuccessAlert(setAlertData, 'Matched successfully!');
            setSelected({});
            fetchPurchaseAgentMatching();
        } catch (error) {
            console.error('Error matching selected:', error);
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const handleSingleMatch = async (row) => {
        const payload = {
            company_id: companyData.id,
            matches: [
                {
                    plaid_transaction_id: row.transaction.id,
                    aggregated_file_inventory_id: row.inventory.id,
                },
            ],
        };

        try {
            setLoading(true);
            await oneToOnePurchaseMatching(payload);
            handleSuccessAlert(setAlertData, 'Matched successfully!');

            fetchPurchaseAgentMatching();
        } catch (error) {
            console.error('Error matching row:', error);
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchaseAgentMatching = async () => {
        if (!companyData?.id) return;

        try {
            setLoading(true);
            const response = await purchaseAgentMatching({
                companyId: companyData.id,
                page,
                rowsPerPage: 50,
                ...filters,
            });

            if (response?.count) {
                setTotalCount(response.count);
            }

            const items = Array.isArray(response)
                ? response
                : Array.isArray(response?.results)
                  ? response.results
                  : [];

            const mappedData = items.map((item, index) => {
                const plaid = item.plaid_transaction || {};
                const agg = item.aggregated_file_inventory || {};

                return {
                    id: item.id ?? index + 1,
                    transaction: {
                        id: plaid.id,
                        amount: Number(plaid.amount ?? 0),
                        date: plaid.date,
                        description: plaid.description,
                        card_last4: plaid.card_data?.card_number || plaid.bank_account_mask,
                        bank_name: plaid.bank_account_name,
                    },
                    inventory: {
                        id: agg.id,
                        description: agg.description,
                        class_1: agg.class_1,
                        class_2: agg.class_2,
                        seats: typeof agg.seats === 'string' ? agg.seats : agg.seats,
                        purchase_email: agg.purchaser_email,
                        company_email: item.company_email, // Added company_email
                        purchase_price: Number(agg.purchase_price ?? 0),
                        purchased_at: agg.purchased_at,
                        reference_id: agg.reference_id,
                        purchase_id: agg.purchase_id,
                    },
                    matchConfidence: item.score_percentage ?? null,
                    matchReason: item.reason_label || 'No reason provided',
                };
            });

            setAllData(mappedData);
            setFilteredData(mappedData);
        } catch (error) {
            console.error('Error in purchase agent matching:', error);
            setAllData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const getMakingMatches = async () => {
        const response = await statusTestAIMatching(companyData?.id);

        if (response.sync_status === 'in-progress') {
            setIsMakingMatches(true);
        } else {
            setIsMakingMatches(false);
        }
    };

    const headCells = [
        {
            id: 'transaction',
            label: 'Transaction',
            render: (row) => (
                <Box sx={{ py: 0.25 }}>
                    <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight={500}
                        sx={{ fontSize: '13px' }}
                    >
                        <span style={{ color: '#16A34A' }}>
                            ${Math.abs(row.transaction.amount).toFixed(2)}
                        </span>{' '}
                        •{' '}
                        {row.transaction.description?.length > truncateLimit ? (
                            <Tooltip title={row.transaction.description} arrow>
                                <span>
                                    {row.transaction.description.substring(0, truncateLimit)}...
                                </span>
                            </Tooltip>
                        ) : (
                            row.transaction.description
                        )}{' '}
                        {row.transaction.purchase_email}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        <span style={{ color: '#16A34A' }}>{row.transaction.date}</span> •
                        {row.transaction.card_last4} • {row.transaction.bank_name}
                    </Typography>
                </Box>
            ),
        },
        {
            id: 'inventory',
            label: 'Purchases',
            render: (row) => (
                <Box sx={{ py: 0.25 }}>
                    <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight={500}
                        sx={{ fontSize: '13px' }}
                    >
                        <span style={{ color: '#16A34A' }}>
                            ${row.inventory.purchase_price.toFixed(2)}
                        </span>{' '}
                        •{' '}
                        {row.inventory.description?.length > truncateLimit ? (
                            <Tooltip title={row.inventory.description} arrow>
                                <span>
                                    {row.inventory.description.substring(0, truncateLimit)}...
                                </span>
                            </Tooltip>
                        ) : (
                            row.inventory.description
                        )}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        <span style={{ color: '#16A34A' }}>{row.inventory.purchased_at}</span> •
                        Section {row.inventory.class_1} • Row {row.inventory.class_2} •{' '}
                        {row.inventory.seats === 1 ? 'Seat' : 'Seats'} {row.inventory.seats}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                        {row.inventory.purchase_email}
                        <Tooltip
                            title={
                                <Box sx={{ p: 1 }}>
                                    <Typography variant="body2" display="block">
                                        <strong>Purchase ID:</strong> {row.inventory.purchase_id}
                                    </Typography>
                                    <Typography variant="body2" display="block">
                                        <strong>Reference ID:</strong> {row.inventory.reference_id}
                                    </Typography>
                                </Box>
                            }
                            arrow
                        >
                            <IconButton size="small" sx={{ ml: 1 }}>
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                </Box>
            ),
        },
        {
            id: 'source',
            label: 'Source',
            align: 'center',
            render: (row) => (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {row.inventory.company_email && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEmailClick(row.inventory.company_email)}
                                sx={{
                                    p: 0.5,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                <SourceIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 0.5 }}>
                        <Tooltip title={row.matchReason} arrow placement="top">
                            <Chip
                                label={`${row.matchConfidence}%`}
                                size="small"
                                color={
                                    row.matchConfidence > 80
                                        ? 'success'
                                        : row.matchConfidence > 50
                                          ? 'warning'
                                          : 'error'
                                }
                                variant="filled"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 20,
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Box>
            ),
        },
        {
            id: 'action',
            label: 'Action',
            align: 'center',
            render: (row) => (
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    fullWidth
                    onClick={() => handleSingleMatch(row)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        py: 0.5,
                    }}
                >
                    Match
                </Button>
            ),
        },
    ];

    useEffect(() => {
        fetchPurchaseAgentMatching();
        getMakingMatches();
    }, [page, companyData?.id, filters]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle search and sorting
    useEffect(() => {
        let filtered = [...allData];

        // Apply sorting
        const sortField = Object.keys(sorting)[0];
        if (sortField) {
            filtered.sort((a, b) => {
                // Handle nested properties if needed
                const aValue = sortField.includes('.')
                    ? sortField.split('.').reduce((o, i) => o[i], a)
                    : a[sortField];

                const bValue = sortField.includes('.')
                    ? sortField.split('.').reduce((o, i) => o[i], b)
                    : b[sortField];

                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setFilteredData(filtered);
        // setPage(1); // Reset to first page on filter/sort change
    }, [allData, sorting, sortOrder]);

    const handleFiltersSubmit = (values) => {
        setFilters(values);
        setPage(1);
    };

    const handleSort = (columnId, order) => {
        setSorting({ [columnId]: order });
        setSortOrder(order);
    };

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `one-to-one-status/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            if (data.one_to_one_status == 'completed') {
                handleSuccessAlert(setAlertData, 'Agent Matching Completed Successfully');

                setIsMakingMatches(false);
                fetchPurchaseAgentMatching();
            }
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData]);

    return (
        <Box sx={{ width: '100%', p: isMobile ? 2 : 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    mb: 3,
                    gap: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    AI Matching Dashboard
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row',
                        width: isMobile ? '100%' : 'auto',
                    }}
                ></Box>
            </Box>

            <Box sx={{ mb: 2 }}>
                <PurchaseAiAgentMatchingFilters
                    isMakingMatches={isMakingMatches}
                    handleFiltersSubmit={handleFiltersSubmit}
                    setShowPaymentIdentifier={setShowPaymentIdentifier}
                />
            </Box>

            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    background: (theme) => (theme.palette.mode == 'dark' ? '#212936' : 'white'),
                }}
            >
                <Box sx={{ p: 0 }}>
                    {currentTab === 0 && (
                        <Box>
                            {Object.keys(selected).length > 0 && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography>
                                        {Object.keys(selected).length} item
                                        {Object.keys(selected).length !== 1 ? 's' : ''} selected
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setSelected({})}
                                        >
                                            Clear selection
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={handleMatchSelected}
                                        >
                                            Match Selected
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            {loading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: 200,
                                    }}
                                >
                                    <CircleLoading />
                                </Box>
                            ) : (
                                <DataTable
                                    headCells={headCells}
                                    data={filteredData}
                                    sorting={sorting}
                                    sortOrder={sortOrder}
                                    sortHandler={handleSort}
                                    selected={selected}
                                    setSelected={setSelected}
                                    isSelectable={true}
                                    canEdit={false}
                                    handleRowClick={(row) => console.log('Row clicked:', row)}
                                    isRowClickable={true}
                                    visibleColumns={headCells.map((cell) => cell.id)}
                                    isLoading={loading}
                                    bulkEdit={false}
                                    showHeaderCheckBox={true}
                                    rowSx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            cursor: 'pointer',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'action.selected',
                                        },
                                        '& > td': {
                                            paddingTop: '5px',
                                            paddingBottom: '5px',
                                        },
                                    }}
                                    sx={{
                                        '& .MuiTableRow-root': {
                                            height: 'auto',
                                            '& > td': {
                                                paddingTop: '2px',
                                                paddingBottom: '2px',
                                                paddingLeft: '0px',
                                                paddingRight: '8px',
                                                '&:first-of-type': {
                                                    paddingLeft: '16px',
                                                },
                                                '&:last-child': {
                                                    // This targets the last column (Action)
                                                    textAlign: 'center',
                                                    '& > div': {
                                                        // This targets the container div in the Action cell
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px',
                                                        width: '100%',
                                                        margin: '0 auto',
                                                    },
                                                    '& .MuiButton-root': {
                                                        // Style for the Match button
                                                        margin: '0 auto',
                                                        display: 'block',
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    )}
                    <Grid sx={{ p: 2 }}>
                        <TablePagination
                            page={page}
                            count={totalCount}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                            rowsPerPage={50}
                        />
                    </Grid>

                    {currentTab === 1 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 400,
                                p: 4,
                            }}
                        >
                            <Typography variant="h6" color="text.secondary">
                                This is for Flag table
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            <EmailSourceModal
                showEmailSourceModal={emailSourceModalOpen}
                setShowEmailSourceModal={setEmailSourceModalOpen}
                emailIds={selectedEmailIds}
            />

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Box>
    );
};

export default PurchaseAiAgentMatching;
